import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, Form, Button, Table, Badge, Alert } from "react-bootstrap";
import { FaPlus, FaEdit, FaTrash, FaArrowLeft, FaSearch, FaMapMarkerAlt, FaCheck, FaTimes, FaBan, FaExclamationTriangle } from "react-icons/fa";
import { useZonas } from "../../../services/hooks/useZonas";
import { zonaApi } from "../../../services/api/zonaApi";

const esZonaActiva = (zona) =>
  zona?.activo === true || zona?.activo === 1 || zona?.activo == null;

const MantenimientoZonas = () => {
  const navigate = useNavigate();

  // Usar el hook personalizado
  const {
    zonas,
    loading,
    error,
    fetchZonas,
    createZona,
    updateZona,
    deleteZona,
    cambiarEstadoZona,
    clearError
  } = useZonas();

  const [state, setState] = useState({
    zonaSeleccionada: null,
    filtro: "",
  });

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [zonaToEdit, setZonaToEdit] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: ''
  });

  const [showEstadoModal, setShowEstadoModal] = useState(false);
  const [zonaEstadoTarget, setZonaEstadoTarget] = useState(null);
  const [validacionInactivacion, setValidacionInactivacion] = useState(null);
  const [loadingValidacionEstado, setLoadingValidacionEstado] = useState(false);
  const [procesandoEstado, setProcesandoEstado] = useState(false);

  // Cargar zonas al montar el componente
  useEffect(() => {
    const selectedProject = JSON.parse(localStorage.getItem('selectedProject'));
    if (selectedProject && selectedProject.id) {
      fetchZonas(selectedProject.id);
    }
  }, [fetchZonas]);

  // Limpiar errores cuando se monta el componente
  useEffect(() => {
    if (error) clearError();
  }, [error, clearError]);

  // Filtrar zonas según el filtro
  const zonasFiltradas = zonas.filter((zona) =>
    zona.nombre.toLowerCase().includes(state.filtro.toLowerCase()) ||
    (zona.descripcion && zona.descripcion.toLowerCase().includes(state.filtro.toLowerCase()))
  );

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({ nombre: '', descripcion: '' });
    setZonaToEdit(null);
  };

  const handleOpenCreateModal = () => {
    setModalMode('create');
    setFormData({ nombre: '', descripcion: '' });
    setZonaToEdit(null);
    setShowModal(true);
  };

  const handleOpenEditModal = (zona) => {
    setModalMode('edit');
    setFormData({
      nombre: zona.nombre,
      descripcion: zona.descripcion || ''
    });
    setZonaToEdit(zona);
    setShowModal(true);
  };

  const handleSaveZona = async (zonaData) => {
    try {
      const selectedProject = JSON.parse(localStorage.getItem('selectedProject'));
      
      if (modalMode === 'create') {
        await createZona({
          ...zonaData,
          proyecto_id: selectedProject.id
        });
      } else {
        await updateZona(zonaToEdit.id, zonaData);
      }
      handleCloseModal();
    } catch (error) {
      console.error('Error al guardar zona:', error);
      // El error ya se maneja en el hook con toast
    }
  };

  const handleDeleteZona = async (zona) => {
    if (window.confirm(`¿Está seguro de eliminar la zona "${zona.nombre}"?`)) {
      try {
        await deleteZona(zona.id);
        setState({ ...state, zonaSeleccionada: null });
      } catch (error) {
        console.error("Error al eliminar zona:", error);
        // El error ya se maneja en el hook con toast
      }
    }
  };

  const handleCerrarModalEstado = () => {
    setShowEstadoModal(false);
    setZonaEstadoTarget(null);
    setValidacionInactivacion(null);
    setLoadingValidacionEstado(false);
    setProcesandoEstado(false);
  };

  const handleAbrirModalEstado = async (zona) => {
    setZonaEstadoTarget(zona);
    setValidacionInactivacion(null);
    setShowEstadoModal(true);

    if (!esZonaActiva(zona)) return;

    setLoadingValidacionEstado(true);
    try {
      const response = await zonaApi.validarInactivacionZona(zona.id);
      setValidacionInactivacion(response.data);
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || "Error al validar la zona";
      toast.error(errorMessage);
      handleCerrarModalEstado();
    } finally {
      setLoadingValidacionEstado(false);
    }
  };

  const handleConfirmarCambioEstado = async () => {
    if (!zonaEstadoTarget) return;

    const activa = esZonaActiva(zonaEstadoTarget);
    if (activa && validacionInactivacion && !validacionInactivacion.puede_inactivar) {
      return;
    }

    setProcesandoEstado(true);
    try {
      const actualizada = await cambiarEstadoZona(zonaEstadoTarget.id, !activa);
      setState((prev) => ({
        ...prev,
        zonaSeleccionada: actualizada,
      }));
      handleCerrarModalEstado();
    } catch (error) {
      console.error("Error al cambiar estado de la zona:", error);
    } finally {
      setProcesandoEstado(false);
    }
  };

  const esInactivacion = zonaEstadoTarget && esZonaActiva(zonaEstadoTarget);
  const puedeConfirmarEstado =
    !procesandoEstado &&
    !loadingValidacionEstado &&
    (!esInactivacion || validacionInactivacion?.puede_inactivar);

  return (
    <>
      {/* Digital Harmony Styles para Mantenimiento Zonas */}
      <style>
        {`
          /* Fondo degradado */
          .dh-maintenance-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
          }
          
          /* Header principal */
          .dh-main-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 2rem 0;
            margin-bottom: 2rem;
            border-radius: 0 0 30px 30px;
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
          }
          
          .dh-main-title {
            font-size: 2.2rem;
            font-weight: 700;
            margin: 0;
          }
          
          .dh-main-subtitle {
            font-size: 1.1rem;
            opacity: 0.9;
            margin: 0.5rem 0 0 0;
          }
          
          /* Cards con Digital Harmony */
          .dh-card {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
          }
          
          .dh-card-header {
            border-radius: 20px 20px 0 0;
            padding: 1.5rem 2rem;
            border: none;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            font-weight: 700;
          }
          
          /* Botones Digital Harmony */
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(17, 153, 142, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-primary:hover {
            background: linear-gradient(135deg, #38ef7d 0%, #2dd4bf 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(17, 153, 142, 0.4);
            color: white;
          }
          
          .btn-dh-warning {
            background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(251, 191, 36, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-warning:hover {
            background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(251, 191, 36, 0.4);
            color: white;
          }
          
          .btn-dh-danger {
            background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(239, 68, 68, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-danger:hover {
            background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(239, 68, 68, 0.4);
            color: white;
          }
          
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(107, 114, 128, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-secondary:hover {
            background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(107, 114, 128, 0.4);
            color: white;
          }
          
          /* Campos de formulario modernos */
          .dh-form-control {
            border: 2px solid #e5e7eb;
            border-radius: 10px;
            padding: 12px 16px;
            font-size: 1rem;
            transition: all 0.3s ease;
            background: rgba(255, 255, 255, 0.9);
          }
          
          .dh-form-control:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
            background: white;
          }
          
          /* Enhanced Table */
          .dh-enhanced-table {
            border-collapse: separate;
            border-spacing: 0;
            border-radius: 15px;
            overflow: hidden;
            box-shadow: 0 4px 15px rgba(0,0,0,0.05);
            background: white;
          }
          
          .dh-enhanced-table thead th {
            background: #374151 !important;
            color: white !important;
            font-weight: 700 !important;
            font-size: 0.875rem !important;
            text-transform: uppercase !important;
            padding: 1rem 0.75rem !important;
            border: none !important;
            position: sticky !important;
            top: 0 !important;
            z-index: 10 !important;
          }
          
          .dh-enhanced-table tbody td {
            padding: 0.75rem;
            border: none;
            border-bottom: 1px solid #f3f4f6;
            font-size: 0.9rem;
            vertical-align: middle;
          }
          
          .dh-enhanced-table tbody tr:hover {
            background: linear-gradient(135deg, #f9fafb 0%, rgba(102, 126, 234, 0.02) 100%) !important;
          }
          
          .dh-enhanced-table tbody tr.selected {
            background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%) !important;
            border-left: 4px solid #667eea;
          }
          
          /* Checkbox personalizado */
          .dh-checkbox {
            width: 20px;
            height: 20px;
            border: 2px solid #d1d5db;
            border-radius: 6px;
            background: white;
            cursor: pointer;
            transition: all 0.2s ease;
          }
          
          .dh-checkbox:checked {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-color: #667eea;
          }
          
          /* Badges mejorados */
          .dh-badge-success {
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            color: white;
            border-radius: 10px;
            padding: 6px 12px;
            font-weight: 600;
            font-size: 0.85rem;
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
          }
          
          .dh-badge-danger {
            background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
            color: white;
            border-radius: 10px;
            padding: 6px 12px;
            font-weight: 600;
            font-size: 0.85rem;
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
          }

          .dh-modal-estado .modal-content {
            border: none;
            border-radius: 20px;
            overflow: hidden;
            box-shadow: 0 20px 50px rgba(0,0,0,0.2);
          }

          .dh-modal-estado .modal-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border: none;
            padding: 1.25rem 1.5rem;
          }

          .dh-modal-estado .modal-header .btn-close {
            filter: brightness(0) invert(1);
          }

          .dh-alert {
            border: none;
            border-radius: 12px;
            padding: 1rem 1.25rem;
          }

          .dh-alert-info {
            background: linear-gradient(135deg, rgba(102, 126, 234, 0.12) 0%, rgba(118, 75, 162, 0.08) 100%);
            color: #374151;
            border-left: 4px solid #667eea;
          }

          .dh-alert-warning {
            background: linear-gradient(135deg, rgba(251, 191, 36, 0.15) 0%, rgba(245, 158, 11, 0.1) 100%);
            color: #92400e;
            border-left: 4px solid #f59e0b;
          }

          .dh-alert-danger {
            background: linear-gradient(135deg, rgba(239, 68, 68, 0.12) 0%, rgba(220, 38, 38, 0.08) 100%);
            color: #991b1b;
            border-left: 4px solid #ef4444;
          }
        `}
      </style>

      <div className="dh-maintenance-bg">
        <ToastContainer />
        
        <div className="dh-main-header">
          <Container>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1 className="dh-main-title">
                  <FaMapMarkerAlt className="me-3" />
                  Mantenimiento de Zonas
                </h1>
                <p className="dh-main-subtitle">Gestión y administración de zonas del campamento</p>
              </div>
              <Button 
                className="btn-dh-secondary d-flex align-items-center"
                onClick={() => navigate("/home")}
              >
                <FaArrowLeft className="me-2" />
                Volver
              </Button>
            </div>
          </Container>
        </div>

        <Container fluid className="py-4">
          <Row>
            <Col lg={12}>
              <Card className="dh-card">
                <Card.Header className="dh-card-header">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Lista de Zonas</h5>
                    <div className="d-flex gap-2">
                      <Button
                        onClick={handleOpenCreateModal}
                        className="btn-dh-primary d-flex align-items-center"
                      >
                        <FaPlus className="me-2" />
                        Nueva Zona
                      </Button>
                      <Button
                        className="btn-dh-warning d-flex align-items-center"
                        onClick={() => {
                          if (state.zonaSeleccionada) {
                            handleOpenEditModal(state.zonaSeleccionada);
                          } else {
                            toast.error("Por favor seleccione una zona para editar", {
                              position: "top-right",
                              autoClose: 2000
                            });
                          }
                        }}
                      >
                        <FaEdit className="me-2" />
                        Editar
                      </Button>
                      <Button
                        className="btn-dh-secondary d-flex align-items-center"
                        onClick={() => {
                          if (state.zonaSeleccionada) {
                            handleAbrirModalEstado(state.zonaSeleccionada);
                          } else {
                            toast.error("Por favor seleccione una zona", {
                              position: "top-right",
                              autoClose: 2000
                            });
                          }
                        }}
                        disabled={!state.zonaSeleccionada}
                      >
                        {state.zonaSeleccionada && !esZonaActiva(state.zonaSeleccionada) ? (
                          <>
                            <FaCheck className="me-2" />
                            Activar
                          </>
                        ) : (
                          <>
                            <FaBan className="me-2" />
                            Inactivar
                          </>
                        )}
                      </Button>
                      <Button
                        className="btn-dh-danger d-flex align-items-center"
                        onClick={() => handleDeleteZona(state.zonaSeleccionada)}
                        disabled={!state.zonaSeleccionada}
                      >
                        <FaTrash className="me-2" />
                        Eliminar
                      </Button>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body style={{ padding: '2rem' }}>
                  <div className="mb-4">
                    <div className="position-relative">
                      <Form.Control
                        type="search"
                        className="dh-form-control"
                        placeholder="Buscar zona..."
                        value={state.filtro}
                        onChange={(e) => setState({ ...state, filtro: e.target.value })}
                        style={{ paddingLeft: '3rem' }}
                      />
                      <FaSearch 
                        className="position-absolute" 
                        style={{ 
                          left: '1rem', 
                          top: '50%', 
                          transform: 'translateY(-50%)', 
                          color: '#6b7280' 
                        }} 
                      />
                    </div>
                  </div>

                  <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    <Table className="dh-enhanced-table">
                      <thead>
                        <tr>
                          <th style={{ width: "50px" }}></th>
                          <th>Nombre</th>
                          <th>Descripción</th>
                          <th className="text-center">Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan="4" className="text-center py-5">
                              <div className="d-flex flex-column align-items-center">
                                <div className="spinner-border text-primary mb-3" role="status">
                                  <span className="visually-hidden">Cargando...</span>
                                </div>
                                <span className="text-muted">Cargando zonas...</span>
                              </div>
                            </td>
                          </tr>
                        ) : zonasFiltradas.length === 0 ? (
                          <tr>
                            <td colSpan="4" className="text-center py-5">
                              <Alert variant="info" className="mb-0">
                                <FaMapMarkerAlt className="me-2" />
                                No se encontraron zonas
                              </Alert>
                            </td>
                          </tr>
                        ) : (
                          zonasFiltradas.map((zona, index) => (
                            <tr 
                              key={zona.id}
                              className={state.zonaSeleccionada?.id === zona.id ? 'selected' : ''}
                              style={{ cursor: 'pointer' }}
                              onClick={() => setState({ ...state, zonaSeleccionada: zona })}
                            >
                              <td>
                                <Form.Check
                                  type="checkbox"
                                  className="dh-checkbox"
                                  checked={state.zonaSeleccionada?.id === zona.id}
                                  onChange={() => {}}
                                />
                              </td>
                              <td style={{ fontWeight: '600', color: '#374151' }}>{zona.nombre}</td>
                              <td style={{ color: '#6b7280' }}>{zona.descripcion || 'Sin descripción'}</td>
                              <td className="text-center">
                                {esZonaActiva(zona) ? (
                                  <Badge className="dh-badge-success">
                                    <FaCheck /> Activo
                                  </Badge>
                                ) : (
                                  <Badge className="dh-badge-danger">
                                    <FaBan /> Inactivo
                                  </Badge>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Modal de Zona */}
      {showModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <FaMapMarkerAlt className="me-2" />
                  {modalMode === 'create' ? 'Nueva Zona' : 'Editar Zona'}
                </h5>
                <button type="button" className="btn-close" onClick={handleCloseModal}></button>
              </div>
              <div className="modal-body">
                <Form onSubmit={(e) => { e.preventDefault(); handleSaveZona(formData); }}>
                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">
                          <FaMapMarkerAlt className="me-2" />
                          Nombre de la Zona *
                        </Form.Label>
                        <Form.Control
                          type="text"
                          className="dh-form-control"
                          placeholder="Ej: Zona Norte, Zona Sur, Zona Administrativa"
                          value={formData.nombre || ''}
                          onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                          required
                          maxLength={50}
                        />
                        <Form.Text className="text-muted">
                          Máximo 50 caracteres
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">
                          <FaMapMarkerAlt className="me-2" />
                          Descripción
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          className="dh-form-control"
                          placeholder="Descripción detallada de la zona..."
                          value={formData.descripcion || ''}
                          onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
                        />
                        <Form.Text className="text-muted">
                          Información adicional sobre la zona (opcional)
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">
                          <FaMapMarkerAlt className="me-2" />
                          Proyecto
                        </Form.Label>
                        <Form.Control
                          type="text"
                          className="dh-form-control"
                          value={JSON.parse(localStorage.getItem('selectedProject'))?.nombre || ''}
                          disabled
                          readOnly
                        />
                        <Form.Text className="text-muted">
                          La zona pertenece al proyecto seleccionado
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                </Form>
              </div>
              <div className="modal-footer">
                <Button className="btn-dh-secondary" onClick={handleCloseModal}>
                  <FaTimes className="me-2" />
                  Cancelar
                </Button>
                <Button 
                  className="btn-dh-primary" 
                  onClick={() => handleSaveZona(formData)}
                  disabled={!formData.nombre || formData.nombre.trim().length === 0}
                >
                  <FaCheck className="me-2" />
                  {modalMode === 'create' ? 'Crear Zona' : 'Actualizar Zona'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal activar / inactivar zona */}
      {showEstadoModal && zonaEstadoTarget && (
        <div className="modal show d-block dh-modal-estado" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title d-flex align-items-center">
                  {esInactivacion ? (
                    <>
                      <FaBan className="me-2" />
                      Inactivar zona
                    </>
                  ) : (
                    <>
                      <FaCheck className="me-2" />
                      Activar zona
                    </>
                  )}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCerrarModalEstado}
                  disabled={procesandoEstado}
                />
              </div>
              <div className="modal-body p-4">
                <p className="mb-3" style={{ color: '#374151', fontSize: '1.05rem' }}>
                  Zona seleccionada: <strong>{zonaEstadoTarget.nombre}</strong>
                </p>

                {esInactivacion ? (
                  <>
                    {loadingValidacionEstado ? (
                      <div className="text-center py-4">
                        <div className="spinner-border text-primary mb-3" role="status">
                          <span className="visually-hidden">Validando...</span>
                        </div>
                        <p className="text-muted mb-0">
                          Verificando reservas y check-ins en curso...
                        </p>
                      </div>
                    ) : validacionInactivacion?.puede_inactivar ? (
                      <Alert className="dh-alert dh-alert-info mb-0">
                        <FaCheck className="me-2" />
                        {validacionInactivacion.mensaje}
                        <div className="mt-2 small text-muted">
                          Al inactivar, todas las camas de la zona quedarán inactivas.
                          Seguirán apareciendo en el reporte de camas como Inactiva.
                        </div>
                      </Alert>
                    ) : (
                      <Alert className="dh-alert dh-alert-danger mb-0">
                        <FaExclamationTriangle className="me-2" />
                        {validacionInactivacion?.mensaje}
                        {(validacionInactivacion?.reservas > 0 ||
                          validacionInactivacion?.check_ins > 0) && (
                          <ul className="mb-0 mt-2 ps-3">
                            {validacionInactivacion.reservas > 0 && (
                              <li>
                                {validacionInactivacion.reservas} reserva
                                {validacionInactivacion.reservas !== 1 ? "s" : ""} en curso
                              </li>
                            )}
                            {validacionInactivacion.check_ins > 0 && (
                              <li>
                                {validacionInactivacion.check_ins} check-in
                                {validacionInactivacion.check_ins !== 1 ? "s" : ""} en curso
                              </li>
                            )}
                          </ul>
                        )}
                      </Alert>
                    )}
                  </>
                ) : (
                  <Alert className="dh-alert dh-alert-warning mb-0">
                    <FaExclamationTriangle className="me-2" />
                    ¿Confirma que desea activar la zona <strong>{zonaEstadoTarget.nombre}</strong>?
                    Volverá a estar disponible en los procesos y reportes del proyecto.
                  </Alert>
                )}
              </div>
              <div className="modal-footer border-0 px-4 pb-4">
                <Button
                  className="btn-dh-secondary"
                  onClick={handleCerrarModalEstado}
                  disabled={procesandoEstado}
                >
                  <FaTimes className="me-2" />
                  {esInactivacion && validacionInactivacion && !validacionInactivacion.puede_inactivar
                    ? "Cerrar"
                    : "Cancelar"}
                </Button>
                {(!esInactivacion || validacionInactivacion?.puede_inactivar) && (
                <Button
                  className={esInactivacion ? "btn-dh-danger" : "btn-dh-primary"}
                  onClick={handleConfirmarCambioEstado}
                  disabled={!puedeConfirmarEstado}
                >
                  {procesandoEstado ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" />
                      Procesando...
                    </>
                  ) : esInactivacion ? (
                    <>
                      <FaBan className="me-2" />
                      Confirmar inactivación
                    </>
                  ) : (
                    <>
                      <FaCheck className="me-2" />
                      Confirmar activación
                    </>
                  )}
                </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MantenimientoZonas;